import {
  default as React,
  useCallback,
  useContext,
  useEffect,
  useState,
  type FC,
} from "react";

import clsx from "clsx";
import { debounce } from "lodash";
import { useTimezoneSelect } from "react-timezone-select";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import { gql } from "@apollo/client";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type Profile as ProfileQuery,
  type UpdateUserDetails,
  type UpdateUserDetailsVariables,
} from "@north-beam/nb-client-side";
import { UPDATE_USER_DETAILS } from "@pages/settings/queries";
import { SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { Select } from "@shared/selects";
import Spinner from "@shared/spinner";
import { TextInput } from "@shared/text-input";

import { SideNavContext } from "..";
import { useOnboardingStep } from "../onboarding-nav/use-onboarding-number";
import { Steps } from "../steps";

import { Step, type StepChildProps } from "./step";
import { StepContainer } from "./step-container";

export const UserProfileCollectionStep: FC<StepChildProps> = ({
  nextStep,
  previousStep,
  isLast,
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const context = React.useContext(SideNavContext);
  const { setSubNavItems } = useContext(SideNavContext);

  const { stepDictionary } = useOnboardingStep();

  if (context?.subNavItems?.length > 0) {
    setSubNavItems([]);
  }
  const { loading, data } = useNorthbeamQuery<ProfileQuery>(PROFILE_QUERY);

  const [updateUserDetails, { loading: updateUserDetailsLoading }] =
    useNorthbeamMutation<UpdateUserDetails, UpdateUserDetailsVariables>(
      UPDATE_USER_DETAILS,
      { refetchQueries: [{ query: PROFILE_QUERY }] },
    );

  const [companyName, setCompanyName] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [region, setRegion] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [domainName, setDomainName] = useState<string>("");
  const [timezone, setTimezone] = useState<string>("");
  useEffect(() => {
    const userData = data?.me;
    if (userData) {
      setCompanyName(userData.companyName || "");
      setFirstName(userData.firstName || "");
      setLastName(userData.lastName || "");
      setDomainName(userData.domainName || "");
      setTimezone(userData.timezone || "");
      setCountry(userData.country || "");
      setRegion(userData.region || "");
      setPostalCode(userData.postalCode || "");
    }
  }, [data]);

  const onSubmitClicked = React.useCallback(async () => {
    const result = await updateUserDetails({
      variables: {
        companyName,
        firstName,
        lastName,
        domainName,
        timezone,
        country,
        region,
        postalCode,
      },
    });
    setDomainName(result.data?.updateUserDetails.domainName || domainName);
  }, [
    companyName,
    firstName,
    lastName,
    domainName,
    timezone,
    country,
    region,
    postalCode,
    updateUserDetails,
  ]);

  const { options } = useTimezoneSelect({});

  const functionDebounce = useCallback(
    debounce((inputCompanyName, currentlySelectedTimezone) => {
      updateUserDetails({
        variables: {
          companyName: inputCompanyName,
          firstName,
          lastName,
          domainName,
          timezone: currentlySelectedTimezone,
          country,
          region,
          postalCode,
        },
      });
    }, 1500),
    [],
  );

  const [_, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    provider: withDefault(StringParam, ""),
  });

  const onNext = useCallback(() => {
    if (companyName.length === 0) {
      setIsConfirmModalOpen(true);
    } else {
      setSearchParams({
        step: Steps.CURRENCY,
      });
      onSubmitClicked();
      nextStep && nextStep();
    }
  }, [companyName.length, nextStep, onSubmitClicked, setSearchParams]);

  return (
    <StepContainer
      isLast={isLast}
      onMoveBackwardButtonClicked={() => {
        setSearchParams({
          step: Steps.ACCOUNT_MANAGERS,
        });
        previousStep && previousStep();
      }}
      title={`${stepDictionary["basic-details"].index}. Enter Business Information`}
      onMoveForwardButtonClicked={() => {
        onNext();
      }}
      knowledgeBaseLink="https://docs.northbeam.io/docs/business-information"
      knowledgeBaseText="Enter Business Information"
    >
      <Step
        nextStepButtonText={`${stepDictionary["currency"].index}. Select Currency`}
        nextStep={() => {
          onNext();
        }}
      >
        <div className="w-[922px] h-[318px] px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex">
          <div className="text-slate-900 text-2xl font-semibold leading-loose">
            Please tell us about your business
          </div>
          <>
            {loading && (
              <div className="row col-8">
                <div className="py-2 my-2">Please wait...</div>
              </div>
            )}
            {!loading && (
              <div className="flex flex-col space-y-6 max-w-md text-sm">
                <div className="flex flex-row">
                  <TextInput
                    label="Company name"
                    value={companyName}
                    onChange={async ({ target }) => {
                      setCompanyName(target.value);
                      functionDebounce(target.value, timezone);
                    }}
                    inputClassName="h-10"
                  />
                  <div className="flex items-center pl-2 pt-4">
                    {updateUserDetailsLoading && <Spinner />}
                  </div>
                </div>
                <div className="nb-input-container">
                  <label className="label">Timezone</label>
                  <Select
                    onChange={(selectedTimeZone) => {
                      updateUserDetails({
                        variables: {
                          companyName,
                          firstName,
                          lastName,
                          domainName,
                          timezone: selectedTimeZone,
                          country,
                          region,
                          postalCode,
                        },
                      });
                      setTimezone(selectedTimeZone);
                    }}
                    isDisabled={updateUserDetailsLoading}
                    isSearchable
                    options={options}
                    value={timezone}
                    className="h-10"
                  />
                </div>
              </div>
            )}
          </>
        </div>
        <Modal
          isOpen={isConfirmModalOpen}
          titleContent={
            <div className="font-bold">
              Complete following steps to mark this step ‘complete’
            </div>
          }
          onClose={() => {
            setIsConfirmModalOpen(false);
          }}
        >
          <div className="h-[320px] overflow-y-auto">
            <div
              className={clsx(
                "w-full py-5 flex items-center justify-between border-b border-[#D9D9D9]",
              )}
            >
              <span className="text-md font-semibold leading-7">
                Company Name
              </span>
              <div className="flex flex-col gap-0.5 rounded-3xl px-5 py-1.5 bg-interstellar-3 justify-start items-center">
                <span className="text-[10px] font-semibold uppercase leading-tight tracking-wide">
                  Incomplete
                </span>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsConfirmModalOpen(false);
                  }}
                  className="text-base leading-none font-semibold text-md no-underline"
                >
                  Go to step
                </a>
              </div>
            </div>
          </div>
          <div className="w-full justify-end flex">
            <SecondaryButton
              variant="tertiary"
              className="font-semibold uppercase"
              onClick={() => {
                setSearchParams({
                  step: Steps.CURRENCY,
                });
                nextStep && nextStep();
              }}
            >
              Skip for now
            </SecondaryButton>
          </div>
        </Modal>
      </Step>
    </StepContainer>
  );
};

export const PROFILE_QUERY = gql`
  query ProfileV2 {
    me {
      id
      firstName
      lastName
      companyName
      domainName
      timezone
      country
      region
      postalCode
    }
  }
`;
